import { type AppConfig, createBlobFetcher, createFetcher, createQueryClient } from '@fuze/portal-core';
import type { QueryClient } from '@tanstack/react-query';

import { configureDocumentClient } from '@/api/documents';
import { configureApiClient } from '@/api/mutator';

import { Experimentation, type ExperimentationClient } from './flags/experimentation';
import { type ErrorReporter, initReporting } from './observability/reporting';

export type AppServices = {
  config: AppConfig;
  reporter: ErrorReporter;
  experimentation: ExperimentationClient;
  queryClient: QueryClient;
};

/**
 * Builds the app's long-lived clients once, before React renders (main.tsx). Outside React on purpose: StrictMode
 * runs state initialisers twice, and these must exist exactly once, with the API client bound to the same query
 * client the app renders with.
 */
export type BootOptions = {
  /** Full-page navigation; tests pass a spy. */
  navigate?: (url: string) => void;
};

export const bootApp = (
  config: AppConfig,
  {
    navigate = (url) => {
      window.location.assign(url);
    },
  }: BootOptions = {},
): AppServices => {
  // Reporting first, so anything thrown while booting the rest has somewhere to go.
  const reporter = initReporting(config);
  const experimentation = Experimentation.create(config);
  const queryClient = createQueryClient({
    // The session ended mid-use (any request but GET /me, which the route guards handle). A full navigation also
    // drops every cached response, so nothing from the old session survives into the login page.
    onUnauthorized: (error) => {
      navigate(error.code === 'SESSION_EXPIRED' ? '/login?reason=session_expired' : '/login');
    },
    onError: (error) => {
      reporter.report(error, { source: 'query' });
    },
  });
  const fetcherOptions = { baseUrl: config.apiBaseUrl };
  configureApiClient(createFetcher(fetcherOptions));
  configureDocumentClient(createBlobFetcher(fetcherOptions));
  return { config, reporter, experimentation, queryClient };
};
